// Restricted JSON (rfc8785-jcs-ascii-safe-integer-v1) parser and
// canonicalizer — TypeScript port for the web client (M6-D2), the fourth
// implementation of the shared canonical-byte contract family alongside C++
// (cpp/game/rulesetidentity.cpp), Python (tools/contract/contract.py), and
// the gateway JS (services/gateway/src/restricted-json.js). The port is
// line-faithful: same error codes, same precedence, same canonical bytes;
// test/restricted-json.test.ts runs the closed vector family
// rulesets/collapse-go/vectors/canonicalization-v1.json against it.
//
// The client needs this for two jobs: canonicalizing outgoing commands
// (submitCommand carries commandCanonicalJson — canonical bytes produced by
// the client and re-verified by the gateway at entry) and fail-closed
// parsing of incoming session bytes. No hashing here: digest duties belong
// to the gateway (the browser has no synchronous SHA-256; the client never
// computes one).

export const CANONICALIZATION_PROFILE = "rfc8785-jcs-ascii-safe-integer-v1";

export const SAFE_INTEGER_MIN = -9007199254740991n;
export const SAFE_INTEGER_MAX = 9007199254740991n;

// Matches the C++/JS nesting cap (the Python oracle rejects slightly
// shallower documents via its recursion limit; the vector family has no
// depth-boundary case and every implementation fails closed on deep input).
export const MAX_JSON_DEPTH = 1024;

// Restricted values: integers are BigInt, objects are null-prototype maps.
export type RestrictedValue =
  | null
  | boolean
  | bigint
  | string
  | RestrictedValue[]
  | { [key: string]: RestrictedValue };

export class RestrictedJsonError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "RestrictedJsonError";
    this.code = code;
  }
}

// Parsed objects carry their original member order in a hidden property.
// ECMAScript reorders integer-like keys ahead of others on plain
// enumeration, which would otherwise scramble the document-order error
// precedence that the C++ parser and Python oracle define. Canonical output
// is unaffected (keys are always sorted), but phase-2 profile checks must
// see the original order.
const MEMBER_ORDER = Symbol("mutago.restrictedJson.memberOrder");

type OrderedObject = { [key: string]: RestrictedValue } & {
  [MEMBER_ORDER]?: string[];
};

function memberOrderOf(value: OrderedObject): string[] {
  return value[MEMBER_ORDER] ?? Object.keys(value);
}

function fail(code: string, message: string): never {
  throw new RestrictedJsonError(code, message);
}

function decodeUtf8(bytes: Uint8Array): string {
  try {
    return new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    fail("invalid-utf8", "input is not valid UTF-8");
  }
}

function isWhitespace(ch: number): boolean {
  return ch === 0x20 || ch === 0x09 || ch === 0x0a || ch === 0x0d;
}

class Parser {
  private readonly text: string;
  private pos = 0;

  constructor(text: string) {
    this.text = text;
  }

  private error(message: string): never {
    fail("invalid-json", message);
  }

  parse(depth: number): RestrictedValue {
    if (depth > MAX_JSON_DEPTH)
      fail("resource-limit", "JSON nesting exceeds the supported depth");
    this.skipWhitespace();
    const value = this.parseValue(depth);
    this.skipWhitespace();
    if (this.pos !== this.text.length)
      this.error("trailing data after top-level JSON value");
    return value;
  }

  private skipWhitespace(): void {
    while (this.pos < this.text.length && isWhitespace(this.text.charCodeAt(this.pos)))
      this.pos++;
  }

  private parseValue(depth: number): RestrictedValue {
    if (depth > MAX_JSON_DEPTH)
      fail("resource-limit", "JSON nesting exceeds the supported depth");
    if (this.pos >= this.text.length)
      this.error("unexpected end of JSON input");
    const ch = this.text.charCodeAt(this.pos);
    if (ch === 0x7b) return this.parseObject(depth + 1); // {
    if (ch === 0x5b) return this.parseArray(depth + 1); // [
    if (ch === 0x22) return this.parseString(); // "
    if (ch === 0x74) return this.parseLiteral("true", true);
    if (ch === 0x66) return this.parseLiteral("false", false);
    if (ch === 0x6e) {
      if (this.text.startsWith("null", this.pos)) return this.parseLiteral("null", null);
      this.error("invalid JSON literal");
    }
    // Only the exact constants NaN / Infinity / -Infinity get the classified
    // code (mirrors the C++ parser); other bare tokens are grammar errors.
    if (
      this.text.startsWith("NaN", this.pos) ||
      this.text.startsWith("Infinity", this.pos) ||
      this.text.startsWith("-Infinity", this.pos)
    )
      fail("invalid-json-number", "non-JSON numeric constant is not allowed");
    if (ch === 0x2d || (ch >= 0x30 && ch <= 0x39)) // - or digit
      return this.parseNumber();
    this.error("unexpected character in JSON input");
  }

  private parseLiteral(word: string, value: RestrictedValue): RestrictedValue {
    if (this.text.startsWith(word, this.pos)) {
      this.pos += word.length;
      return value;
    }
    this.error(`invalid literal, expected ${word}`);
  }

  // Number scanning mirrors the C++ RestrictedJsonParser exactly: scan the
  // integer digits, then consume a fraction/exponent only when it is
  // well-formed (a trailing "." or "1e" is left unconsumed and becomes a
  // grammar error at the enclosing level); floating-point is reported before
  // the safe-range check, so "9007199254740992.0" is floating-point while
  // "9007199254740992." is unsafe-integer.
  private parseNumber(): bigint {
    const start = this.pos;
    if (this.text.charCodeAt(this.pos) === 0x2d) {
      // '-'
      this.pos++;
      if (this.pos >= this.text.length) this.error("JSON number ends after '-'");
    }
    const digitRun = (from: number): number => {
      let end = from;
      while (end < this.text.length) {
        const ch = this.text.charCodeAt(end);
        if (ch < 0x30 || ch > 0x39) break;
        end++;
      }
      return end;
    };
    let intEnd: number;
    const firstDigit = this.text.charCodeAt(this.pos);
    if (firstDigit === 0x30) {
      // Leading zero must stand alone: "01" is a grammar violation.
      intEnd = this.pos + 1;
      const next = this.text.charCodeAt(intEnd);
      if (next >= 0x30 && next <= 0x39)
        this.error("JSON integers cannot have leading zeroes");
    } else if (firstDigit >= 0x31 && firstDigit <= 0x39) {
      intEnd = digitRun(this.pos);
    } else {
      this.error("invalid number in JSON input");
    }
    let hasFloatingPointSyntax = false;
    let end = intEnd;
    if (
      this.text.charCodeAt(end) === 0x2e && // '.'
      this.text.charCodeAt(end + 1) >= 0x30 &&
      this.text.charCodeAt(end + 1) <= 0x39
    ) {
      hasFloatingPointSyntax = true;
      end = digitRun(end + 2);
    }
    const expMark = this.text.charCodeAt(end);
    if (expMark === 0x65 || expMark === 0x45) {
      // 'e' / 'E'
      let expStart = end + 1;
      const sign = this.text.charCodeAt(expStart);
      if (sign === 0x2b || sign === 0x2d) expStart++; // + or -
      const expDigit = this.text.charCodeAt(expStart);
      if (expDigit >= 0x30 && expDigit <= 0x39) {
        hasFloatingPointSyntax = true;
        end = digitRun(expStart);
      }
    }
    const token = this.text.slice(start, end);
    this.pos = end;
    if (hasFloatingPointSyntax)
      fail("floating-point", `floating-point JSON numbers are not allowed: ${token}`);
    const value = BigInt(token); // exact for arbitrary length; "-0" -> 0n
    if (value < SAFE_INTEGER_MIN || value > SAFE_INTEGER_MAX)
      fail("unsafe-integer", `integer is outside the safe signed range: ${token}`);
    return value;
  }

  // Parses a string literal and returns its decoded content. Escape
  // sequences are normalized here; ASCII enforcement happens in the walk.
  private parseString(): string {
    this.pos++; // opening quote
    let out = "";
    for (;;) {
      if (this.pos >= this.text.length)
        this.error("unterminated string in JSON input");
      const ch = this.text.charCodeAt(this.pos);
      if (ch === 0x22) {
        // closing quote
        this.pos++;
        return out;
      }
      if (ch === 0x5c) {
        // backslash escape
        const esc = this.text.charCodeAt(this.pos + 1);
        switch (esc) {
          case 0x22: out += '"'; this.pos += 2; break;
          case 0x5c: out += "\\"; this.pos += 2; break;
          case 0x2f: out += "/"; this.pos += 2; break;
          case 0x62: out += "\b"; this.pos += 2; break;
          case 0x66: out += "\f"; this.pos += 2; break;
          case 0x6e: out += "\n"; this.pos += 2; break;
          case 0x72: out += "\r"; this.pos += 2; break;
          case 0x74: out += "\t"; this.pos += 2; break;
          case 0x75: {
            const hex = this.text.slice(this.pos + 2, this.pos + 6);
            if (hex.length !== 4 || !/^[0-9a-fA-F]{4}$/.test(hex))
              this.error("invalid \\u escape in JSON string");
            out += String.fromCharCode(parseInt(hex, 16));
            this.pos += 6;
            break;
          }
          default:
            this.error("invalid escape in JSON string");
        }
        continue;
      }
      if (ch < 0x20)
        this.error("unescaped control character in JSON string");
      out += this.text[this.pos];
      this.pos++;
    }
  }

  private parseObject(depth: number): OrderedObject {
    if (depth > MAX_JSON_DEPTH)
      fail("resource-limit", "JSON nesting exceeds the supported depth");
    this.pos++; // {
    const result: OrderedObject = Object.create(null);
    const memberOrder: string[] = [];
    Object.defineProperty(result, MEMBER_ORDER, { value: memberOrder });
    const seen = new Set<string>();
    // Duplicate keys are reported when the object CLOSES, not at the second
    // occurrence: a parse error inside a member fires first, exactly as in
    // the C++ parser and the Python oracle's object_pairs_hook.
    let firstDuplicateKey: string | null = null;
    this.skipWhitespace();
    if (this.text.charCodeAt(this.pos) === 0x7d) {
      // }
      this.pos++;
      return result;
    }
    for (;;) {
      this.skipWhitespace();
      if (this.text.charCodeAt(this.pos) !== 0x22)
        this.error("object key must be a string");
      const key = this.parseString();
      const duplicate = seen.has(key);
      seen.add(key);
      memberOrder.push(key);
      this.skipWhitespace();
      if (this.text.charCodeAt(this.pos) !== 0x3a) // :
        this.error("expected ':' after object key");
      this.pos++;
      this.skipWhitespace();
      const child = this.parseValue(depth);
      if (!duplicate) result[key] = child;
      else if (firstDuplicateKey === null) firstDuplicateKey = key;
      this.skipWhitespace();
      const sep = this.text.charCodeAt(this.pos);
      if (sep === 0x2c) {
        // ,
        this.pos++;
        continue;
      }
      if (sep === 0x7d) {
        // }
        this.pos++;
        if (firstDuplicateKey !== null)
          fail("duplicate-key", `duplicate object key: ${JSON.stringify(firstDuplicateKey)}`);
        return result;
      }
      this.error("expected ',' or '}' in object");
    }
  }

  private parseArray(depth: number): RestrictedValue[] {
    if (depth > MAX_JSON_DEPTH)
      fail("resource-limit", "JSON nesting exceeds the supported depth");
    this.pos++; // [
    const result: RestrictedValue[] = [];
    this.skipWhitespace();
    if (this.text.charCodeAt(this.pos) === 0x5d) {
      // ]
      this.pos++;
      return result;
    }
    for (;;) {
      result.push(this.parseValue(depth));
      this.skipWhitespace();
      const sep = this.text.charCodeAt(this.pos);
      if (sep === 0x2c) {
        // ,
        this.pos++;
        this.skipWhitespace();
        continue;
      }
      if (sep === 0x5d) {
        // ]
        this.pos++;
        return result;
      }
      this.error("expected ',' or ']' in array");
    }
  }
}

function isNonAscii(text: string): boolean {
  for (let i = 0; i < text.length; i++)
    if (text.charCodeAt(i) > 0x7f) return true;
  return false;
}

// Phase 2: profile walk. Mirrors the C++ deferred-error precedence: within
// an object, a non-ASCII key beats any child error (non-ascii-key first);
// among children the first error in member/element order wins.
function validateProfileValue(value: RestrictedValue): void {
  if (value === null || value === true || value === false) return;
  if (typeof value === "bigint") return; // range checked during parse
  if (typeof value === "string") {
    if (isNonAscii(value))
      fail("non-ascii-string", "non-ASCII string is not allowed");
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) validateProfileValue(item);
    return;
  }
  if (typeof value === "object") {
    const keys = memberOrderOf(value as OrderedObject);
    for (const key of keys) {
      if (isNonAscii(key))
        fail("non-ascii-key", "non-ASCII object key is not allowed");
    }
    for (const key of keys) validateProfileValue((value as OrderedObject)[key]!);
    return;
  }
  fail("unsupported-json-type", `unsupported value type: ${typeof value}`);
}

function escapeStringCanonical(text: string): string {
  let out = '"';
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    if (ch === 0x22) out += '\\"';
    else if (ch === 0x5c) out += "\\\\";
    else if (ch === 0x08) out += "\\b";
    else if (ch === 0x09) out += "\\t";
    else if (ch === 0x0a) out += "\\n";
    else if (ch === 0x0c) out += "\\f";
    else if (ch === 0x0d) out += "\\r";
    else if (ch < 0x20) out += "\\u" + ch.toString(16).padStart(4, "0");
    else out += text[i];
  }
  return out + '"';
}

function emitCanonical(value: RestrictedValue, parts: string[]): void {
  if (value === null) {
    parts.push("null");
    return;
  }
  if (value === true) {
    parts.push("true");
    return;
  }
  if (value === false) {
    parts.push("false");
    return;
  }
  if (typeof value === "bigint") {
    parts.push(value.toString());
    return;
  }
  if (typeof value === "string") {
    parts.push(escapeStringCanonical(value));
    return;
  }
  if (Array.isArray(value)) {
    parts.push("[");
    for (let i = 0; i < value.length; i++) {
      if (i > 0) parts.push(",");
      emitCanonical(value[i]!, parts);
    }
    parts.push("]");
    return;
  }
  const keys = Object.keys(value).sort();
  parts.push("{");
  for (let i = 0; i < keys.length; i++) {
    if (i > 0) parts.push(",");
    parts.push(escapeStringCanonical(keys[i]!));
    parts.push(":");
    emitCanonical((value as OrderedObject)[keys[i]!]!, parts);
  }
  parts.push("}");
}

// Parses UTF-8 JSON bytes (or a string) under the restricted profile and
// returns the value. Integers come back as BigInt; objects are
// null-prototype objects so keys like "__proto__" cannot pollute prototypes.
export function parseRestrictedJson(bytes: Uint8Array | string): RestrictedValue {
  const text = typeof bytes === "string" ? bytes : decodeUtf8(bytes);
  const value = new Parser(text).parse(0);
  validateProfileValue(value);
  return value;
}

// Canonicalizes an already-parsed restricted value to canonical text (the
// profile guarantees ASCII, so the canonical UTF-8 bytes are the char codes).
export function canonicalizeRestricted(value: RestrictedValue): string {
  validateProfileValue(value);
  const parts: string[] = [];
  emitCanonical(value, parts);
  return parts.join("");
}

// Parse + canonicalize in one pass: the canonical byte string for the input.
export function canonicalizeRestrictedJsonBytes(bytes: Uint8Array | string): string {
  return canonicalizeRestricted(parseRestrictedJson(bytes));
}
